import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyPutDevicesAsMissingQuery, useLazyPutDevicesReturnFromMissingQuery } from "store/api/adminApi"
import { useAppSelector } from "store/hooks"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconMissing } from "assets/svg/modal-icon-missing.svg"

import getErrorMessage from "utils/getErrorMessage"
import { ModalLayout } from "layouts/ModalLayout"

interface EnterpriseMissingModalProps {
  onCancel: () => void
  onSuccess: () => void
}

export const EnterpriseMissingModal = ({ onCancel, onSuccess }: EnterpriseMissingModalProps) => {
  const [triggerPutDevicesAsMissing, { isFetching }] = useLazyPutDevicesAsMissingQuery()
  const selectedDeviceIDs = useAppSelector(state => state.tempData.selectedDeviceIDs)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    try {
      const res = await triggerPutDevicesAsMissing({ device_ids: selectedDeviceIDs })
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success("The status of the selected thermostat(s) was changed successfully!")
        onSuccess()
        onCancel()
      }
    } catch (error) {
      console.error(error)
    }
  }

  return createPortal(
    <ModalLayout width="450px" isFetching={isFetching} onConfirm={onSubmit} onCancel={onCancel}>
      <div className={classNames(styles.modalContent, styles.center)}>
        <IconMissing />
        <h3 className={styles.modalTitle}>Mark as missing</h3>
        <div className={styles.modalMessage}>
          By changing the status of the selected thermostat(s) to “Missing”, you confirm that these thermostat(s) are no
          longer in your Stock. Are you sure you want to change the status of the selected thermostat(s)?
        </div>
      </div>
    </ModalLayout>,
    document.body,
  )
}

interface EnterpriseReturnFromMissingModalProps {
  onCancel: () => void
  onSuccess: () => void
}

export const EnterpriseReturnFromMissingModal = ({ onCancel, onSuccess }: EnterpriseReturnFromMissingModalProps) => {
  const [triggerReturnFromMissing, { isFetching }] = useLazyPutDevicesReturnFromMissingQuery()
  const selectedDeviceIDs = useAppSelector(state => state.tempData.selectedDeviceIDs)

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    try {
      const res = await triggerReturnFromMissing({ device_ids: selectedDeviceIDs })
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success("The status of the selected thermostat(s) was changed successfully!")
        onSuccess()
        onCancel()
      }
    } catch (error) {
      console.error(error)
    }
  }

  return createPortal(
    <ModalLayout width="450px" isFetching={isFetching} onConfirm={onSubmit} onCancel={onCancel}>
      <div className={classNames(styles.modalContent, styles.center)}>
        <IconMissing />
        <h3 className={styles.modalTitle}>Return from missing</h3>
        <div className={styles.modalMessage}>
          Are you sure you want to return the selected thermostat(s) from missing to inventory?
        </div>
      </div>
    </ModalLayout>,
    document.body,
  )
}
